import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider, useTheme } from '@/context/ThemeContext';
import LoginPage from '@/pages/LoginPage';
import TranslatorDashboard from '@/pages/translator/Dashboard';
import TranslatorProfile from '@/pages/translator/Profile';
import TranslatorNDA from '@/pages/translator/NDA';
import TranslatorAssignments from '@/pages/translator/Assignments';
import TranslatorInvoices from '@/pages/translator/Invoices';
import TranslatorWork from '@/pages/translator/Work';
import AdminDashboard from '@/pages/admin/Dashboard';
import AdminTranslators from '@/pages/admin/Translators';
import AdminProjects from '@/pages/admin/Projects';
import AdminAssignments from '@/pages/admin/Assignments';
import AdminSubmissions from '@/pages/admin/Submissions';
import AdminInvoices from '@/pages/admin/Invoices';
import ContributorAnalytics from '@/pages/admin/ContributorAnalytics';
import MapPro from '@/pages/admin/MapPro';
import InvitePage from '@/pages/admin/InvitePage';
import TeamProjects from '@/pages/admin/TeamProjects';
import OnboardPage from '@/pages/OnboardPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import OwnerDashboard from '@/pages/owner/Dashboard';
import OwnerFinance from '@/pages/owner/Finance';
import OwnerClientBillings from '@/pages/owner/ClientBillings';
import OwnerClientProjects from '@/pages/owner/ClientProjects';
import WhatsInProgress from '@/pages/owner/WhatsInProgress';
import OwnerPayments from '@/pages/owner/Payments';
import OwnerSettings from '@/pages/owner/Settings';
import GtsProEmails from '@/pages/owner/GtsProEmails';
import TeamRevenue from '@/pages/owner/TeamRevenue';
import ProjectsBills from '@/pages/owner/ProjectsBills';
import ProtectedRoute from '@/components/ProtectedRoute';
import '@/App.css';

// Themed App wrapper that applies the theme class to the app container
function ThemedApp() {
  const { themeMode, animationsEnabled } = useTheme();
  
  return (
    <MotionConfig reducedMotion={animationsEnabled ? 'never' : 'always'}>
      <div className={`App ${themeMode}`} data-theme={themeMode}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/onboard" element={<OnboardPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* Translator Routes */}
          <Route path="/translator" element={<ProtectedRoute allowedRoles={['translator']} />}>
            <Route index element={<TranslatorDashboard />} />
            <Route path="profile" element={<TranslatorProfile />} />
            <Route path="nda" element={<TranslatorNDA />} />
            <Route path="assignments" element={<TranslatorAssignments />} />
            <Route path="work" element={<TranslatorWork />} />
            <Route path="invoices" element={<TranslatorInvoices />} />
          </Route>
          
          {/* Admin Routes */}
          <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin', 'owner']} />}>
            <Route index element={<AdminDashboard />} />
            <Route path="translators" element={<AdminTranslators />} />
            <Route path="projects" element={<AdminProjects />} />
            <Route path="client-projects" element={<OwnerClientProjects />} />
            <Route path="in-progress" element={<WhatsInProgress />} />
            <Route path="assignments" element={<AdminAssignments />} />
            <Route path="submissions" element={<AdminSubmissions />} />
            <Route path="invoices" element={<AdminInvoices />} />
            <Route path="contributors" element={<ContributorAnalytics />} />
            <Route path="map-pro" element={<MapPro />} />
            <Route path="invite" element={<InvitePage />} />
            <Route path="gts-pro-emails" element={<GtsProEmails />} />
            <Route path="team-projects" element={<TeamProjects />} />
            <Route path="projects-bills" element={<ProjectsBills />} />
          </Route>
          
          {/* Owner Routes */}
          <Route path="/owner" element={<ProtectedRoute allowedRoles={['owner']} />}>
            <Route index element={<OwnerDashboard />} />
            <Route path="finance" element={<OwnerFinance />} />
            <Route path="in-progress" element={<WhatsInProgress />} />
            <Route path="projects" element={<OwnerClientProjects />} />
            <Route path="billings" element={<OwnerClientBillings />} />
            <Route path="payments" element={<OwnerPayments />} />
            <Route path="gts-pro" element={<GtsProEmails />} />
            <Route path="projects-bills" element={<ProjectsBills />} />
            <Route path="team-revenue" element={<TeamRevenue />} />
            <Route path="settings" element={<OwnerSettings />} />
          </Route>
          
          <Route path="/" element={<Navigate to="/login" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
    </MotionConfig>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ThemedApp />
    </ThemeProvider>
  );
}

export default App;
